import { type ReactNode, useState } from "react";
import type {
	CircleLayerSpecification,
	HeatmapLayerSpecification,
	LineLayerSpecification,
} from "maplibre-gl";

import type { TContextFeature, TOperationalFeature } from "safesystem_geoserver-api";

import { GeodataContext } from "./geodataContextHelpers";
import { contextLayerId, geoServer, targetSchemaVersion } from "@/lib/config";
import {
	EIdentify,
	EPrioritize,
	EProduct,
	ESafeRoadsModule,
	type ILayerSource,
	type ISystemLayers,
	type IFeature,
	type TLayer,
	type TLayerPaint,
	type TLayerId,
	type TVersionRecord,
	type TModuleRecord,
	type TProductRecord,
	type TModule,
	ECrashes,
	EAccidents,
} from "@/models/saferoads/interfaces";
import { FetchStatus } from "@/lib/defines";
import { getLogger } from "@/lib/logApi";
import { layerTypeToQueryPropertiesMap } from "@/models/saferoads/columns";

import layoutFarsAccidentHeatmap from "@/models/common/layer_styles/fars_accident_heatmap_layout.json";
import layoutFarsAccidentCircle from "@/models/common/layer_styles/fars_accident_circle_layout.json";
import layoutIdentify from "@/models/saferoads/layer_styles/saferoads_identify_layout.json";
import layoutPrioritize from "@/models/saferoads/layer_styles/saferoads_prioritize_layout.json";
import paintFarsAccidentHeatmap from "@/models/common/layer_styles/fars_accident_heatmap_paint.json";
import paintFarsAccidentHeatmapOnSelection from "@/models/common/layer_styles/fars_accident_heatmap_paint_on-selection.json";
import paintFarsAccidentCircle from "@/models/common/layer_styles/fars_accident_circle_paint.json";
import paintFarsAccidentCircleOnSelection from "@/models/common/layer_styles/fars_accident_circle_paint_on-selection.json";
import paintIdentify from "@/models/saferoads/layer_styles/saferoads_identify_paint.json";
import paintIdentifyOnSelection from "@/models/saferoads/layer_styles/saferoads_identify_paint_on-selection.json";
import paintPrioritize from "@/models/saferoads/layer_styles/saferoads_prioritize_paint.json";
import paintPrioritizeOnSelection from "@/models/saferoads/layer_styles/saferoads_prioritize_paint_on-selection.json";

import layoutCustomCrashes from "@/models/common/layer_styles/custom_crashes_circle_layout.json";
import paintCustomCrashes from "@/models/common/layer_styles/custom_crashes_circle_paint.json";
import paintCustomCrashesOnSelection from "@/models/common/layer_styles/custom_crashes_circle_paint_on-selection.json";

const APP_PRODUCT = EProduct.SafeRoads;
const TARGET_SCHEMA_VERSION = targetSchemaVersion; // from ENV var
const FARS_PRODUCT = EProduct.FARS;
const FARS_SCHEMA_VERSION = "NF_v1";

const log = getLogger();

export function GeodataProvider({ children }: { children: ReactNode }) {
	const [contextLayer, setContextLayer] = useState<TContextFeature>({} as TContextFeature);
	const [featureCounts, setFeatureCounts] = useState<Record<TOperationalFeature["id"], number>>({});
	const [fetchStatus, setFetchStatus] = useState<FetchStatus>(FetchStatus.IDLE);
	const [systemData, setSystemData] = useState({} as ISystemLayers);
	const [systemDataReady, setSystemDataReady] = useState(false);

	async function getGeodata(apikey: string) {
		setFetchStatus(FetchStatus.LOADING);
		try {
			const operationalLayers = (await geoServer.getOperationalLayers(apikey, true)).filter(({ namespace }) =>
				[TARGET_SCHEMA_VERSION, FARS_SCHEMA_VERSION].includes(namespace)
			);
			log.info(`[geoServer.getOperationalLayers] Retrieved ${operationalLayers.length} operational layers`);

			// convert list of layers to Products
			const products = {} as TProductRecord;
			const layerFeatures = operationalLayers.map((layer) => {
				const { keywords, namespace } = layer;
				const product = keywords.filter((k) => k.startsWith("product|"))[0].split("|")[1] as EProduct;
				const module = keywords.filter((k) => k.startsWith("module|"))[0].split("|")[1] as TModule;
				const explorerLayer = { product, module, ...layer };
				try {
					products[product][namespace][module].push(explorerLayer);
				} catch {
					try {
						products[product][namespace][module] = [explorerLayer];
					} catch {
						try {
							products[product][namespace] = {} as TModuleRecord;
							products[product][namespace][module] = [explorerLayer];
						} catch {
							products[product] = {} as TVersionRecord;
							products[product][namespace] = {} as TModuleRecord;
							products[product][namespace][module] = [explorerLayer];
						}
					}
				}
				return explorerLayer;
			});

			if (layerFeatures.length > 1) {
				setFetchStatus(FetchStatus.SUCCESS);
				parseSystemData(products);
				getFeatureCounts(layerFeatures);
			} else if (layerFeatures.length > 0) {
				throw new Error(`Some layers successfully loaded, but count was ${layerFeatures.length} - likely only FARS data has loaded due to SC schema version mismatch`);
			} else {
				throw new Error("No layers loaded.");
			}
		} catch {
			setFetchStatus(FetchStatus.FAILURE);
		}

		getContextLayer(apikey);
	}

	async function getContextLayer(apikey: string) {
		const contextLayer = await geoServer.getContextLayer(apikey, contextLayerId, true);
		if (contextLayer) {
			setContextLayer(contextLayer);
			log.info(`[geoServer.getContextLayer] Retrieved '${contextLayer.id}'`);
		} else {
			log.error("[geoServer.getContextLayer] Could not retrieve context layer...");
		}
	}

	async function getFeatureCounts(features: IFeature[]) {
		const countsArray = await Promise.all(
			features.map(async (layer) => {
				const featureCount = await geoServer.getFeatureCount(layer);
				return { [layer.id]: featureCount };
			})
		);
		const counts = countsArray.reduce((accum, featureCount) => {
			return { ...accum, ...featureCount };
		}, {});
		setFeatureCounts(counts);
	}

	async function parseSystemData(products: TProductRecord) {
		if (
			Object.keys(products).length > 0 &&
			products[APP_PRODUCT] !== undefined &&
			products[FARS_PRODUCT] !== undefined
		) {
			setSystemData({
				[ESafeRoadsModule.Identify]: {
					[EIdentify.Identify]: getVectorLayerSource(
						EIdentify.Identify,
						products[APP_PRODUCT][TARGET_SCHEMA_VERSION][ESafeRoadsModule.Identify][0],
						{
							type: "line",
							layout: layoutIdentify as LineLayerSpecification["layout"],
							paint: paintIdentify as LineLayerSpecification["paint"],
							minzoom: 6,
						},
						3,
						paintIdentifyOnSelection as LineLayerSpecification["paint"]
					),
				},
				[ESafeRoadsModule.Prioritize]: {
					[EPrioritize.Prioritize]: getVectorLayerSource(
						EPrioritize.Prioritize,
						products[APP_PRODUCT][TARGET_SCHEMA_VERSION][ESafeRoadsModule.Prioritize][0],
						{
							type: "line",
							layout: layoutPrioritize as LineLayerSpecification["layout"],
							paint: paintPrioritize as LineLayerSpecification["paint"],
							minzoom: 1,
						},
						0,
						paintPrioritizeOnSelection as LineLayerSpecification["paint"]
					),
				},
				[ECrashes.Accidents]: {
					[EAccidents.AccidentsHeatmap]: getVectorLayerSource(
						EAccidents.AccidentsHeatmap,
						products[FARS_PRODUCT][FARS_SCHEMA_VERSION][ECrashes.Accidents][0],
						{
							type: "heatmap",
							layout: layoutFarsAccidentHeatmap as HeatmapLayerSpecification["layout"],
							paint: paintFarsAccidentHeatmap as HeatmapLayerSpecification["paint"],
							minzoom: 6,
							maxzoom: 11,
						},
						2,
						paintFarsAccidentHeatmapOnSelection as HeatmapLayerSpecification["paint"]
					),
					[EAccidents.AccidentsCircle]: getVectorLayerSource(
						EAccidents.AccidentsCircle,
						products[FARS_PRODUCT][FARS_SCHEMA_VERSION][ECrashes.Accidents][0],
						{
							type: "circle",
							layout: layoutFarsAccidentCircle as CircleLayerSpecification["layout"],
							paint: paintFarsAccidentCircle as CircleLayerSpecification["paint"],
							minzoom: 9,
						},
						1,
						paintFarsAccidentCircleOnSelection as CircleLayerSpecification["paint"]
					),
					[EAccidents.UploadedCrashes]: getGeoJSONLayerSource(
						EAccidents.UploadedCrashes,
						{
							type: "circle",
							layout: layoutCustomCrashes as CircleLayerSpecification["layout"],
							paint: paintCustomCrashes as CircleLayerSpecification["paint"],
						},
						2,
						paintCustomCrashesOnSelection as CircleLayerSpecification["paint"]
					)
				},
			});
			setSystemDataReady(true);
		}
	}

	const contextValue = {
		contextLayer,
		fetchStatus,
		featureCounts,
		getGeodata,
		systemData,
		systemDataReady,
	};

	return <GeodataContext.Provider value={contextValue}>{children}</GeodataContext.Provider>;
}

function getVectorLayerSource(
	layerId: TLayerId,
	feature: IFeature,
	layerProps: Partial<TLayer>,
	zIndex: number,
	layerPaintOnSelect: TLayerPaint
): ILayerSource {
	const vectorSourceId = `${layerId}-source`;
	const sourceLayerId = feature.id.split(":")[1];
	return {
		id: layerId,
		zIndex,
		title: feature.title,
		bbox: feature.latLonBbox,
		source: {
			type: "vector",
			tiles: [
				decodeURI(feature.services.wmts.layer.template).replace(
					"{format}",
					"application/vnd.mapbox-vector-tile"
				),
			],
			attribution: feature.attribution,
		},
		layer: {
			...layerProps,
			id: `${feature.id}|${layerId}`,
			source: vectorSourceId,
			// a vector tile can have multiple layers within the tile, this selects the specific layer, note that our setup only has the one layer per source
			"source-layer": sourceLayerId,
		},
		layerPaintOnSelect,
		wfsRootUrl: (feature.services.wfs.capabilities as string).split("?")[0],
		/** feature properties included in matching search query */
		queryProperties: [...layerTypeToQueryPropertiesMap[layerId]],
	};
}

function getGeoJSONLayerSource(
	layerId: TLayerId,
	layerProps: Partial<TLayer>,
	zIndex: number,
	layerPaintOnSelect: TLayerPaint
): ILayerSource {
	const sourceId = `${layerId}-source`;
	return {
		id: layerId,
		zIndex,
		title: layerId,
		source: {
			type: "geojson",
			// @ts-expect-error IDK
			data: {},
			promoteId: "id"
		},
		layer: {
			...layerProps,
			id: `${layerId}`,
			source: sourceId,
		},
		layerPaintOnSelect,
		/** feature properties included in matching search query */
		queryProperties: [...layerTypeToQueryPropertiesMap[layerId]],
	};
}
